import * as React from 'react'

import { Alert, Box, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { MultiDocumentAssignerFormEmployeesDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import type { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import type { SelectionMetadata } from 'modules/core/components/EntitySelectorV2/types'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'
import { useQuery } from 'modules/core/hooks/useQuery'
import app from 'modules/core/store/models/app'

import EsignatureRequester from 'modules/documents/components/BulkFiles/DocumentAssigner/EsignatureRequester'
import AssignConfirmationModal from 'modules/documents/components/BulkFiles/DocumentAssigner/MultiDocumentAssignerForm/AssignConfirmationModal'
import { Document as DocumentEntity } from 'modules/documents/hooks/documents/useGetDocuments'
import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import { UploadProcess } from 'modules/documents/hooks/useGetUploadProcess'
import {
  UploadProcessDocument,
  useGetUploadProcessDocuments,
} from 'modules/documents/hooks/useGetUploadProcessDocuments'
import { useSendInBulkUpdate } from 'modules/documents/hooks/useSendInBulkUpdate'
import { isTemplate } from 'modules/documents/lib/Documents'
import { ESIGNATURE_MAX_FILE_SIZE } from 'modules/documents/lib/constants'
import { isPersonalFilesWorkflow } from 'modules/documents/lib/distributionWorkflows'
import type { AssignEsignatureForm } from 'modules/documents/lib/forms/AssignEsignatureForm'
import scores from 'modules/documents/store/collections/scores'
import type Score from 'modules/documents/store/models/Score'

const SUGGESTED_SCORE_THRESHOLD = 10
const SELECTED_SCORE_THRESHOLD = 100

type EsignatureRequesterProps = React.ComponentProps<typeof EsignatureRequester>

const getScoreEmployeeIds = (document: Document, scoreFn: (score: Score) => boolean): string[] => {
  return scores
    .filter((score) => String(score.attributes.document_id) === document.id)
    .filter(scoreFn)
    .map((score: Score) => String(score.attributes.employee_id))
}

interface Props {
  document: Document
  selectedIdsForEsignature: string[]
  onSelectIdsForEsignature: (arg0: string[]) => void
  requestEsignatureForm: AssignEsignatureForm
  uploadProcess: UploadProcess
}

const MultiDocumentAssignerForm = ({
  document,
  selectedIdsForEsignature,
  requestEsignatureForm,
  onSelectIdsForEsignature,
  uploadProcess,
}: Props) => {
  const [loadingScores, setLoadingScores] = React.useState(false)
  const [loadingEmployees, setLoadingEmployees] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const [selectedIds, setSelectedIds] = React.useState<string[]>([])
  const [confirmedEmployeeIds, setConfirmedEmployeeIds] = React.useState<string[] | undefined>(
    undefined
  )
  const [suggestedEmployeeIds, setSuggestedEmployeeIds] = React.useState<string[] | undefined>(
    undefined
  )
  const [employeeSelection, setEmployeeSelection] =
    React.useState<EmployeeSelectorV2MultiSelectState | null>(null)
  const [selectionCount, setSelectionCount] = React.useState(0)
  const [uploadProcessDocument, setUploadProcessDocument] = React.useState<
    UploadProcessDocument | undefined
  >()

  const isSelectAll = employeeSelection?.type === 'all'
  const selectedCount = isSelectAll ? selectionCount : selectedIds.length

  // Fetch employee data (names, policies) for the manually selected IDs
  const { data: employeesData } = useQuery(MultiDocumentAssignerFormEmployeesDocument, {
    variables: { ids: selectedIds },
    skip: selectedIds.length === 0 || isSelectAll,
  })

  const employeeNodes = employeesData?.employees?.employees ?? []

  const assignLabel = (() => {
    if (selectedCount === 0) {
      return i18n.t('split_documents.assign_documents.select_employees')
    }
    if (isSelectAll) {
      return i18n.tp('split_documents.assign_documents.send_to', {
        count: selectedCount,
      })
    }
    return i18n.tp('split_documents.assign_documents.send_to', {
      count: selectedIds.length,
      employee: employeeNodes.map((e) => e.firstName ?? ''),
    })
  })()

  const { loading: loadingUploadProcessDocuments, fetch: fetchUploadProcessDocuments } =
    useGetUploadProcessDocuments()

  const fetchCurrentUploadProcessDocument = useCallback(async () => {
    const currentUploadProcessDocument = await fetchUploadProcessDocuments({
      documentIds: [document.id],
    })
    return currentUploadProcessDocument?.[0]
  }, [document.id])

  const refreshUploadProcessDocument = async () => {
    const currentUploadProcessDocument = await fetchCurrentUploadProcessDocument()
    setUploadProcessDocument(currentUploadProcessDocument)
    return currentUploadProcessDocument
  }

  const { submit: onSubmitSendInBulk } = useSendInBulkUpdate()

  useEffect(() => {
    const gatherScores = async () => {
      setLoadingScores(true)
      await scores.fetch({
        data: {
          document_ids: [document.id],
        },
      })

      const confirmedIds = getScoreEmployeeIds(
        document,
        (score: Score) => score.attributes.score >= SELECTED_SCORE_THRESHOLD
      )
      const suggestedIds = getScoreEmployeeIds(
        document,
        (score: Score) =>
          score.attributes.score >= SUGGESTED_SCORE_THRESHOLD &&
          score.attributes.score < SELECTED_SCORE_THRESHOLD
      )
      setConfirmedEmployeeIds(confirmedIds)
      setSuggestedEmployeeIds(suggestedIds)

      setLoadingScores(false)
    }

    setSelectedIds([])
    setEmployeeSelection(null)
    setSelectionCount(0)

    if (isPersonalFileWorkflow) {
      gatherScores()
    }
    refreshUploadProcessDocument().then((currentUploadProcessDocument) => {
      const matchedEmployeeId = currentUploadProcessDocument?.matchedEmployeeId
      if (matchedEmployeeId) {
        const ids = [String(matchedEmployeeId)]
        setSelectedIds(ids)
        setEmployeeSelection({ type: 'manual', ids })
        setSelectionCount(1)
      }
    })
  }, [document.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const onAssignDocument = async () => {
    const requestEsignature = uploadProcess.esignatureRequest ?? false
    setLoadingEmployees(true)
    app.setProgress({
      message: i18n.t('common.sending'),
    })

    if (requestEsignature && document.fileSize && document.fileSize > ESIGNATURE_MAX_FILE_SIZE) {
      setLoadingEmployees(false)
      app.setProgress(null)
      app.setFlash({
        type: 'wrong',
        message: i18n.t('documents.beta.file_errors.max_file_size_esignature'),
      })
      return
    }

    if (!isSelectAll && selectedIds.length === 0) {
      setLoadingEmployees(false)
      app.setProgress(null)
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
      throw new Error("Can't assign document to null employee")
    }

    const esignatureRequest = uploadProcess.esignatureRequest ?? false
    const additionalSigners =
      (uploadProcessDocument?.additionalSigners &&
      uploadProcessDocument?.additionalSigners.length > 0
        ? uploadProcessDocument?.additionalSigners.map((signer) => signer.id)
        : uploadProcess?.additionalSigners.map((signer) => signer.id)) || []

    const employeeSelectionInput = isSelectAll
      ? {
          type: 'all' as const,
          filters: employeeSelection.filters,
          excludedIds: employeeSelection.excludedIds,
        }
      : {
          type: 'manual' as const,
          ids: selectedIds,
        }

    const result = await onSubmitSendInBulk({
      id: document.id,
      byPendingAssignment: true,
      distributionMethod: 'bulk',
      employeeIds: isSelectAll ? [] : selectedIds,
      employeeSelection: employeeSelectionInput,
      isPendingAssignment: false,
      public: true,
      requestEsignature: esignatureRequest,
      signeeIds: additionalSigners,
    })

    const handled = handleResult(result, () => undefined)

    const documentIds =
      handled.success && handled.data.documents?.map((doc: DocumentEntity) => doc.id)

    if (documentIds) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('split_documents.bulk_modal.loading.description'),
      })
    }

    setLoadingEmployees(false)
    app.setProgress(null)
  }

  const onConfirm = () => {
    setOpen(false)
    onAssignDocument()
  }

  const onClose = () => {
    setOpen(false)
  }

  const onSubmit: EsignatureRequesterProps['onClick'] = async () => {
    if (!isSelectAll && selectedIds.length <= 1 && !isTemplate(document)) {
      await onAssignDocument()
    } else {
      setOpen(true)
    }
  }

  const setAssignerId = (value: string | null | undefined) => {
    if (!value) return
    if (selectedIds.includes(value)) return setSelectedIds(selectedIds.filter((id) => id !== value))
    if (isPersonalFileWorkflow) return setSelectedIds([value])

    setSelectedIds([...selectedIds, value])
  }

  const isPersonalFileWorkflow = isPersonalFilesWorkflow(uploadProcess)
  const loading = loadingScores || loadingEmployees || loadingUploadProcessDocuments
  const isDisabled = (() => {
    if (loading) return true
    if (isSelectAll) return selectedCount === 0
    return !selectedIds || selectedIds.length === 0
  })()

  if (loading) return <Loading />

  return (
    <Box flexDirection="column" height="full">
      <Box flexGrow>
        <Box flexDirection="column" padding="s16" minHeight="s64" justifyContent="center">
          <Text.Normal weight="medium">
            {i18n.t('split_documents.assign_documents.recipients_description_v2')}
          </Text.Normal>
        </Box>
        <Box padding="s16" paddingTop="s0" data-testid="employee-selector-container">
          {isPersonalFileWorkflow ? (
            <EmployeeSelectorV2
              value={selectedIds[0]}
              onChange={(id: string | undefined) => {
                setSelectedIds(id != null ? [id] : [])
                setEmployeeSelection(id != null ? { type: 'manual', ids: [id] } : null)
                setSelectionCount(id != null ? 1 : 0)
              }}
              forcedFilters={{
                confirmedEmployeeIds,
                suggestedEmployeeIds,
              }}
              options={{ disableSelectAll: true, asList: true }}
            />
          ) : (
            <EmployeeSelectorV2
              multiple
              value={selectedIds}
              onChange={(
                state: EmployeeSelectorV2MultiSelectState,
                metadata: SelectionMetadata
              ) => {
                setEmployeeSelection(state)
                setSelectionCount(metadata.selectedCount)
                if (state.type === 'manual') {
                  setSelectedIds(state.ids)
                } else {
                  // When type is 'all', clear selectedIds since we use employeeSelection directly
                  setSelectedIds([])
                }
              }}
              options={{ asList: true }}
            />
          )}
        </Box>
      </Box>
      <Box
        padding="s16"
        gap="s16"
        background="grey200"
        border={{
          top: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
        style={{ bottom: '0', position: 'sticky' }}
      >
        {isPersonalFileWorkflow && (
          <Box gap="s16">
            <Alert color="warning">
              <Text.Normal color="grey1000">
                {i18n.t('split_documents.assign_documents.personal_files_alert')}
              </Text.Normal>
            </Alert>
          </Box>
        )}
        <EsignatureRequester
          selectedIdsForEsignature={selectedIdsForEsignature}
          recipientsCount={selectedCount}
          requestEsignatureForm={requestEsignatureForm}
          onSelectIdsForEsignature={onSelectIdsForEsignature}
          uploadProcess={uploadProcess}
          uploadProcessDocument={uploadProcessDocument}
          onSignersUpdateFinished={refreshUploadProcessDocument}
          disabled={isDisabled}
          onClick={onSubmit}
          label={assignLabel}
        />
      </Box>
      {open && (
        <AssignConfirmationModal
          fileName={document.filename}
          employeeNames={
            isSelectAll
              ? []
              : employeeNodes
                  .map((employee) =>
                    getFullDisplayName({
                      firstName: employee.firstName ?? '',
                      lastName: employee.lastName ?? '',
                      canSharePreferredName: employee._policies?.sharePreferredName?.can ?? false,
                      preferredName: employee.preferredName,
                    })
                  )
                  .filter((name): name is string => name !== undefined)
          }
          employeeCount={isSelectAll ? selectedCount : undefined}
          requestEsignature={requestEsignatureForm.get('requestEsignature').value}
          signeesCount={selectedIdsForEsignature.length}
          onClose={onClose}
          onConfirm={onConfirm}
          document={document}
        />
      )}
    </Box>
  )
}

export default observer(MultiDocumentAssignerForm)
